/**
 * ESCHER FALLS — リトグラフ調マテリアルの単一の供給元。MODULE-CONTRACTS §6 / CORE-SPEC §6。
 *
 * **シングルトン**。ピースごとに新規生成しないこと（`getMaterials()` を毎回呼んでよい）。
 * 影（shadowMap）は使わず、面の向きによる3階調のフラットシェーディングで版画らしさを出す。
 */

import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

/**
 * CORE-SPEC §6 の色（three.js 用の 0xrrggbb）。
 *
 * **着せ替え（`game/skins.ts`）で中身が差し替わる。**
 * 参照側は `PALETTE.water` のように**そのつど引く**こと。
 * 分割代入で控えると、着替えても古い色を持ち続ける。
 */
export const PALETTE: {
  paper: number;
  paperShade: number;
  ink: number;
  inkSoft: number;
  stoneLight: number;
  stoneMid: number;
  stoneDark: number;
  water: number;
  accent: number;
} = {
  paper: 0xf2efe6,
  paperShade: 0xe6e2d6,
  ink: 0x2b2823,
  inkSoft: 0x6b665c,
  stoneLight: 0xffffff,
  stoneMid: 0xd2cec1,
  stoneDark: 0x8a8578,
  // 上3つは CORE-SPEC §6 が定める「**画面に出る**階調の範囲」であって、
  // マテリアルに入れる基準色ではない。基準色は陰影で必ず暗くなる方向に転ぶので、
  // 出力が #8a8578 を下回らないよう、下の BASE_* で持ち上げてある（§階調の調整）。
  water: 0x4a8c8a,
  accent: 0xc8622d,
};

export interface MaterialSet {
  /** 石・建物の面。3階調のフラットシェーディング（トゥーン）。 */
  readonly stone: THREE.Material;
  /** 中間調の石。モールディング・帯石など、隣り合う部材と値を分けたいもの。 */
  readonly stoneMid: THREE.Material;
  /** 装飾の柱など、少し沈めたいもの。 */
  readonly stoneDark: THREE.Material;
  /** 水。唯一の彩度。半透明にしない（版画調のベタ）。 */
  readonly water: THREE.Material;
  /** クリア時の水（アクセント色）。 */
  readonly waterClear: THREE.Material;
  /**
   * 輪郭線（細線）。MODULE-CONTRACTS §6 が要求する型をそのまま残してある。
   * 実際の描画は太さを持てる `outlineInk` を使う（下記の理由）。
   */
  readonly outline: THREE.LineBasicMaterial;
  /**
   * 輪郭線の本体。`LineSegments2` 用。
   *
   * `LineBasicMaterial` は WebGL の仕様上 `linewidth` が効かず、必ず 1 デバイスピクセルになる。
   * DPR 2 の画面では 0.5 CSS px となり、版画の線としては細すぎて消えてしまう。
   * `LineMaterial` なら CSS ピクセル単位で太さを指定できる（`resolution` に
   * **CSS サイズ**を入れた場合。`render/meshes.ts` の `onBeforeRender` で毎フレーム同期する）。
   */
  readonly outlineInk: LineMaterial;
  /** ?debug=1 のポート可視化用（深度テスト無効・常に手前）。 */
  readonly debugPort: THREE.Material;
  dispose(): void;
}

/**
 * 3階調を切る `dot(N, L)` の境界。ライトは `(3,8,5)` 固定（CORE-SPEC §6）なので
 * 各面の `dot(N,L)` は +y:0.81 / +z:0.51 / +x:0.30 / -x:-0.30 / -z:-0.51 / -y:-0.81 になる。
 * この境界だと「屋根＝最明」「手前側の壁＝中間」「背を向けた面＝暗」に落ちる。
 *
 * ※ 3階調・単一ライトである以上、view 0 と view 2 では見えている2つの側面が
 *   同じ階調になる（`+x` と `+z` はどちらも中間調）。ここを階調で割ろうとすると
 *   今度は上面が側面と同値になり「上向きの面が最明」が崩れる。
 *   したがって面の切り分けは**輪郭線と目地線**に担わせる方針を採る。
 */
const TONE_BREAKS: readonly [number, number] = [-0.05, 0.55];

/**
 * 3階調の明るさ（0..255）。
 *
 * **最暗を 0 にしてはならない。** かつて `[0, 112, 255]` としており、
 * 実測で建物の支配色が `#6d695e` (109,105,94) まで沈んで、
 * CORE-SPEC §6 の下限 `#8a8578` (138,133,120) を大きく割っていた。
 * 紙色 `#f2efe6` の上に黒い塊が乗っている見た目になり、
 * エッシャーのリトグラフ（全体に明るく、線で形を語る）から遠かった。
 *
 * 暗さで立体を作らず、**輪郭線と目地線**に形を担わせる方針なので、
 * 3階調の幅はこの程度で足りる。
 */
const TONE_STEPS: readonly [number, number, number] = [150, 205, 255];

/** gradientMap の解像度。境界位置を細かく決めるためだけの分割数（階調は3つのまま）。 */
const GRADIENT_SIZE = 32;

/**
 * 面をわずかに奥へ押しやる量。
 *
 * 輪郭線は面の稜線と**完全に同一の座標**にあるため、これを入れないと深度が引き分けになり
 * 線がほぼ全て消える（実機で確認済み。修正前は 492 本引いていて 1 本も見えていなかった）。
 * `factor` 側が効く：面の深度勾配に比例した押し込みなので、等角投影の斜めの面でも
 * 太らせた線（`LineSegments2` はスクリーン空間に広がる板）が確実に手前へ出る。
 */
const FACE_DEPTH_BIAS = 2;

/** 輪郭線の太さ（CSS ピクセル）。DPR に依存しない。 */
const INK_WIDTH_PX = 1.35;

/**
 * トゥーン階調のルックアップテーブル。
 * three.js は `u = dot(N,L) * 0.5 + 0.5` でこのテクスチャを引くので、
 * texel の中心が表す `dot(N,L)` を逆算して3階調に量子化する。
 */
function createToonGradient(): THREE.DataTexture {
  const data = new Uint8Array(GRADIENT_SIZE);
  for (let i = 0; i < GRADIENT_SIZE; i++) {
    const dotNL = ((i + 0.5) / GRADIENT_SIZE) * 2 - 1;
    const step = dotNL > TONE_BREAKS[1] ? 2 : dotNL > TONE_BREAKS[0] ? 1 : 0;
    data[i] = TONE_STEPS[step];
  }
  const tex = new THREE.DataTexture(data, GRADIENT_SIZE, 1, THREE.RedFormat);
  tex.minFilter = THREE.NearestFilter; // 階調を滑らかにしない（版画のベタ塗り）
  tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * 石の面用トゥーンマテリアル。
 *
 * emissive は「紙に刷ったインクの下地」を持ち上げるための底上げ。
 * ライト強度が CORE-SPEC で固定されているため、これがないと最明部が白に届かない。
 *
 * flatShading は指定しない：MeshToonMaterial は three r180 でこのプロパティを持たず
 * （@types/three にも無い）、かつ角柱・箱ジオメトリの法線は元々面ごとに一定なので
 * 指定しなくても見た目は同じベタ塗りになる。
 */
function createSurface(
  color: number,
  emissive: number,
  gradientMap: THREE.DataTexture,
): THREE.MeshToonMaterial {
  return new THREE.MeshToonMaterial({
    color,
    emissive,
    gradientMap,
    polygonOffset: true,
    polygonOffsetFactor: FACE_DEPTH_BIAS,
    polygonOffsetUnits: FACE_DEPTH_BIAS,
  });
}

/**
 * マテリアルに入れる基準色。
 *
 * 陰影（`TONE_STEPS` × ライト）で必ず暗くなる方向に転ぶため、
 * **出力の階調が CORE-SPEC §6 の範囲に収まるよう、基準色は目標より明るく取る。**
 * ここを PALETTE の値そのままにすると、最暗面が目標の下限を割る。
 */
let BASE_STONE = 0xffffff;
let BASE_STONE_MID = 0xe4e0d5;
let BASE_STONE_DARK = 0xc2bdb0;
let BASE_WATER = 0x3c7c7a;

/**
 * 発光の底上げ。
 *
 * かつて `0x4a4740` 前後を入れて最明部を白に届かせていたが、
 * これは全面を一律に持ち上げるので階調が濁る。
 * `TONE_STEPS` の底を上げた今は不要なので、ごく僅かに留める。
 */
const EMISSIVE_STONE = 0x141310;

let cached: MaterialSet | null = null;

/** 着せ替えが渡してくる色。`game/skins.ts` の `Skin['colors']` と同じ形。 */
export interface PaletteOverride {
  readonly paper: number;
  readonly paperShade: number;
  readonly ink: number;
  readonly inkSoft: number;
  readonly water: number;
  readonly accent: number;
  readonly baseStone: number;
  readonly baseStoneMid: number;
  readonly baseStoneDark: number;
  readonly baseWater: number;
}

/**
 * 配色を差し替える。**マテリアルは作り直さず、色だけ差し替える。**
 *
 * 作り直すと、既に組んだメッシュが古いインスタンスを掴んだままになり、
 * 着替えても盤面だけ変わらない。色を書き換えれば全部に即座に効く。
 */
export function applyPalette(c: PaletteOverride): void {
  PALETTE.paper = c.paper;
  PALETTE.paperShade = c.paperShade;
  PALETTE.ink = c.ink;
  PALETTE.inkSoft = c.inkSoft;
  PALETTE.water = c.water;
  PALETTE.accent = c.accent;
  BASE_STONE = c.baseStone;
  BASE_STONE_MID = c.baseStoneMid;
  BASE_STONE_DARK = c.baseStoneDark;
  BASE_WATER = c.baseWater;

  if (cached === null) return; // まだ作られていない。次の生成で新しい色が入る
  const set = (m: THREE.Material, hex: number): void => {
    const withColor = m as THREE.Material & { color?: THREE.Color };
    withColor.color?.setHex(hex);
  };
  set(cached.stone, BASE_STONE);
  set(cached.stoneMid, BASE_STONE_MID);
  set(cached.stoneDark, BASE_STONE_DARK);
  set(cached.water, BASE_WATER);
  set(cached.waterClear, PALETTE.accent);
  set(cached.outline, PALETTE.ink);
  set(cached.outlineInk, PALETTE.ink);
  set(cached.debugPort, PALETTE.accent);
}

/** 初回呼び出しで生成し、以後は同じインスタンスを返す。 */
export function getMaterials(): MaterialSet {
  if (cached) return cached;

  const gradientMap = createToonGradient();

  const stone = createSurface(BASE_STONE, EMISSIVE_STONE, gradientMap);
  const stoneMid = createSurface(BASE_STONE_MID, EMISSIVE_STONE, gradientMap);
  const stoneDark = createSurface(BASE_STONE_DARK, EMISSIVE_STONE, gradientMap);
  // 建物が明るくなったぶん、水は少し沈めて対比を作る。唯一の彩度なので埋もれさせない。
  const water = createSurface(BASE_WATER, 0x0e2423, gradientMap);
  const waterClear = createSurface(PALETTE.accent, 0x2a1408, gradientMap);

  const outline = new THREE.LineBasicMaterial({ color: PALETTE.ink });

  const outlineInk = new LineMaterial({
    color: PALETTE.ink,
    linewidth: INK_WIDTH_PX,
    // renderer が antialias:true なので MSAA のカバレッジで線がなめらかに乗る。
    alphaToCoverage: true,
    worldUnits: false,
  });

  const debugPort = new THREE.MeshBasicMaterial({
    color: PALETTE.accent,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    opacity: 0.95,
    toneMapped: false,
  });

  const set: MaterialSet = {
    stone,
    stoneMid,
    stoneDark,
    water,
    waterClear,
    outline,
    outlineInk,
    debugPort,
    dispose(): void {
      for (const m of [
        stone,
        stoneMid,
        stoneDark,
        water,
        waterClear,
        outline,
        outlineInk,
        debugPort,
      ]) {
        m.dispose();
      }
      gradientMap.dispose();
      if (cached === set) cached = null;
    },
  };

  cached = set;
  return set;
}
